import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { euro, t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["pending", "preparing", "ready", "served", "paid", "cancelled"];
const STATUS_CLASS = {
  pending: "bg-amber-100 text-amber-800",
  preparing: "bg-blue-100 text-blue-800",
  ready: "bg-emerald-100 text-emerald-800",
  served: "bg-slate-200 text-slate-800",
  paid: "bg-green-600 text-white",
  cancelled: "bg-red-200 text-red-800",
};

export default function OrdersLive({ restaurant }) {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const wsRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/orders");
    setOrders(data);
  };

  useEffect(() => {
    load();
    if (!restaurant?.id) return;
    const url = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + `/api/ws/orders/${restaurant.id}`;
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "new_order") {
            toast.success(`Nuovo ordine • ${msg.order.table_label}`);
            setOrders((prev) => [msg.order, ...prev.filter((o) => o.id !== msg.order.id)]);
          } else if (msg.type === "order_updated") {
            setOrders((prev) => prev.map((o) => (o.id === msg.order.id ? msg.order : o)));
          }
        } catch {}
      };
    } catch {}
    return () => wsRef.current?.close();
  }, [restaurant?.id]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
    } catch (e) { toast.error("Aggiornamento fallito"); }
  };

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div className="space-y-6" data-testid="orders-live-page">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Ordini live</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Sala &amp; Cucina</h1>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56" data-testid="orders-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{t.order_status[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((o) => (
          <div key={o.id} className="p-5 bg-card border border-border rounded-2xl space-y-3" data-testid={`order-card-${o.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-display text-2xl font-bold">{o.table_label}</div>
                <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleTimeString("it-IT")}</div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${STATUS_CLASS[o.status] || "bg-secondary"}`} data-testid={`order-status-${o.id}`}>
                {t.order_status[o.status] || o.status}
              </span>
            </div>
            <div className="space-y-1">
              {o.items.map((it, i) => (
                <div key={i} className="text-sm">
                  <div className="flex justify-between">
                    <span>{it.quantity}× {it.name}</span>
                    <span className="text-muted-foreground">{euro(it.line_total, o.currency)}</span>
                  </div>
                  {it.notes ? (
                    <div className="text-xs text-muted-foreground italic pl-4" data-testid={`order-item-note-${o.id}-${i}`}>
                      ↳ {it.notes}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            {o.notes ? (
              <div
                className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2"
                data-testid={`order-note-${o.id}`}
              >
                <span className="font-semibold uppercase tracking-wider text-[10px] block mb-0.5">Nota cliente</span>
                {o.notes}
              </div>
            ) : null}
            {o.customer_name && <div className="text-xs text-muted-foreground">Cliente: {o.customer_name}</div>}
            <div className="flex justify-between items-center pt-2 border-t border-border">
              <div className="font-display text-lg font-bold">{euro(o.total, o.currency)}</div>
              <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                <SelectTrigger className="w-40 h-8 text-xs" data-testid={`order-status-select-${o.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{t.order_status[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full p-12 text-center text-muted-foreground border border-dashed rounded-2xl">
            Nessun ordine.
          </div>
        )}
      </div>
    </div>
  );
}
